import { useState, useEffect, useCallback } from 'react';
import { PreCallPlan } from '../models/AppPreCallPlan';
import { AppPreCallPlanService } from '../services/appPreCallPlanService';

export const useAppPreCallPlans = () => {
    const [preCallPlans, setPreCallPlans] = useState<PreCallPlan[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const fetchPreCallPlans = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await AppPreCallPlanService.getAllPreCallPlans();
            setPreCallPlans(data);
            setTotal(data?.length || 0);
        } catch (err) {
            setError('Failed to fetch pre-call plans');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchPreCallPlansForToday = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await AppPreCallPlanService.getPreCallPlansForToday();
            return data;
        } catch (err) {
            setError('Failed to fetch today\'s pre-call plans');
            console.error(err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchPreCallPlansForThisWeek = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await AppPreCallPlanService.getPreCallPlansForThisWeek();
            return data;
        } catch (err) {
            setError('Failed to fetch this week\'s pre-call plans');
            console.error(err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchPreCallPlansByDateRange = useCallback(async (startDate: string, endDate: string) => {
        setLoading(true);
        setError(null);
        try {
            const data = await AppPreCallPlanService.getPreCallPlansByDateRange(startDate, endDate);
            return data;
        } catch (err) {
            setError('Failed to fetch pre-call plans by date range');
            console.error(err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchPreCallPlansPaginated = useCallback(
        async (
            page: number,
            limit: number,
            search?: string,
            warehouseId?: string | number,
            userId?: string | number
        ) => {
            setLoading(true);
            setError(null);
            try {
                const { plans, total } = await AppPreCallPlanService.getPreCallPlansPaginated(page, limit, search, warehouseId, userId);
                setPreCallPlans(plans);
                setTotal(total);
                return { plans, total };
            } catch (err) {
                setError('Failed to fetch pre-call plans');
                console.error(err);
            } finally {
                setLoading(false);
            }
        },
        []
    );

    const fetchPreCallPlanById = async (id: number) => {
        setLoading(true);
        setError(null);
        try {
            const data = await AppPreCallPlanService.getPreCallPlanById(id);
            return data;
        } catch (err) {
            setError('Failed to fetch pre-call plan by ID.');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const addPreCallPlan = async (newPreCallPlan: Omit<PreCallPlan, 'id' | 'created_time' | 'updated_time'>) => {
        try {
            const createdPreCallPlan = await AppPreCallPlanService.createPreCallPlan(newPreCallPlan);
            setPreCallPlans(prev => [...prev, createdPreCallPlan]);
            return createdPreCallPlan;
        } catch (err) {
            setError('Failed to add pre-call plan');
            console.error(err);
        }
    };

    const updatePreCallPlan = async (id: number, updatedPreCallPlan: Omit<PreCallPlan, 'id' | 'created_time' | 'updated_time'>) => {
        try {
            const updatedRes = await AppPreCallPlanService.updatePreCallPlan(id, updatedPreCallPlan);
            setPreCallPlans(prev => prev.map(plan => (plan.id === id ? updatedRes : plan)));
            return updatedRes;
        } catch (err) {
            setError('Failed to update pre-call plan');
            console.error(err);
        }
    };

    const deletePreCallPlan = async (id: number) => {
        try {
            await AppPreCallPlanService.deletePreCallPlan(id);
            setPreCallPlans(prev => prev.filter(plan => plan.id !== id));
        } catch (err) {
            setError('Failed to delete pre-call plan');
            console.error(err);
        }
    };

    useEffect(() => {
        fetchPreCallPlans();
    }, [fetchPreCallPlans]);

    return {
        preCallPlans,
        total,
        loading,
        error,
        fetchPreCallPlans,
        fetchPreCallPlansForToday,
        fetchPreCallPlansForThisWeek,
        fetchPreCallPlansByDateRange,
        fetchPreCallPlansPaginated,
        fetchPreCallPlanById,
        addPreCallPlan,
        updatePreCallPlan,
        deletePreCallPlan
    };
};
